import React, { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { createPortal } from "react-dom";
import { AgreementsDataType } from "@/types/AgreementsDataType";
import FormCheckbox from "@/components/formControls/FormCheckbox";
import FormHidden from "@/components/formControls/FormHidden";
import Modal from "@/components/ui/Modal";

export default function FormAgreements({
  agreements,
  form,
  checkAll,
}: {
  agreements: undefined | Array<AgreementsDataType>;
  form: UseFormReturn<any, any, undefined>;
  checkAll?: string;
}): React.ReactElement {
  const [openedModal, setOpenedModal] = useState<string | null>(null);
  const [openedGroup, setOpenedGroup] = useState<Array<string>>([]);

  const handleToggleGroupVisibility = (
    e: React.MouseEvent<HTMLAnchorElement>,
    agreementId: string,
  ): void => {
    e.preventDefault();
    setOpenedGroup((prevState) =>
      prevState.indexOf(agreementId) !== -1
        ? prevState.filter((state) => state !== agreementId)
        : [...prevState, agreementId],
    );
  };

  const handleModalOpen = (
    e: React.MouseEvent<HTMLButtonElement>,
    agreementId: string,
  ): void => {
    e.preventDefault();
    setOpenedModal(openedModal === agreementId ? null : agreementId);
  };

  const handleModalClose = (): void => {
    setOpenedModal(null);
  };

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name && name.startsWith("agreementGroup_") && type === "change") {
        const agreementGroup =
          agreements?.filter((agreement: AgreementsDataType) => {
            return (
              agreement.agreements && name === "agreementGroup_" + agreement.id
            );
          })[0] ?? null;
        if (agreementGroup) {
          agreementGroup.agreements!.forEach((agreement: AgreementsDataType) =>
            !agreement.isStatement
              ? form.setValue(
                  "agreement" + agreement.id,
                  value["agreementGroup_" + agreementGroup.id] ?? false,
                )
              : null,
          );
        }
      }

      if (
        checkAll &&
        agreements &&
        name &&
        name === "agreementCheckAll" &&
        type === "change"
      ) {
        agreements.forEach((agreement: AgreementsDataType) => {
          if (!agreement.isStatement) {
            form.setValue(
              agreement.agreements
                ? "agreementGroup_" + agreement.id
                : "agreement" + agreement.id,
              value["agreementCheckAll"] ?? false,
            );
          }
          if (agreement.agreements) {
            agreement.agreements.forEach(
              (groupAgreement: AgreementsDataType) =>
                !groupAgreement.isStatement
                  ? form.setValue(
                      "agreement" + groupAgreement.id,
                      value["agreementCheckAll"] ?? false,
                    )
                  : null,
            );
          }
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, agreements]);

  return (
    <>
      {agreements?.length ? (
        <div>
          {checkAll ? (
            <FormCheckbox name="agreementCheckAll" register={form.register}>
              <span className="font-bold">{checkAll}</span>
            </FormCheckbox>
          ) : (
            ""
          )}
          <div className={"space-y-4 " + (checkAll ? "ml-6 mt-4" : "")}>
            {agreements.map((agreement: AgreementsDataType) => (
              <div key={"agreement" + agreement.id}>
                {agreement.agreements ? (
                  <div className="space-y-4">
                    <FormCheckbox
                      name={"agreementGroup_" + agreement.id}
                      register={form.register}
                    >
                      <div>{agreement.content}</div>
                      <a
                        className="cursor-pointer"
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                          handleToggleGroupVisibility(e, agreement.id)
                        }
                      >
                        [Kliknij tutaj by sprawdzić lub dostosować zakres zgód]
                      </a>
                    </FormCheckbox>
                    <div
                      className={
                        "ml-6 " +
                        (openedGroup.indexOf(agreement.id) !== -1
                          ? ""
                          : "hidden")
                      }
                    >
                      <FormAgreements
                        agreements={agreement.agreements}
                        form={form}
                      />
                    </div>
                  </div>
                ) : agreement.isStatement ? (
                  <div className="text-xs text-[#66676C]">
                    <FormHidden
                      name={"agreement" + agreement.id}
                      value="1"
                      register={form.register}
                      error={form.formState.errors["agreement" + agreement.id]}
                    />
                    <div
                      dangerouslySetInnerHTML={{ __html: agreement.content }}
                    ></div>
                  </div>
                ) : (
                  <FormCheckbox
                    name={"agreement" + agreement.id}
                    register={form.register}
                    error={form.formState.errors["agreement" + agreement.id]}
                  >
                    {agreement.content}&nbsp;
                    {agreement.description && (
                      <button
                        type={"button"}
                        className="cursor-pointer font-bold underline"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                          handleModalOpen(e, agreement.id)
                        }
                      >
                        Rozwiń
                      </button>
                    )}
                  </FormCheckbox>
                )}
                {agreement.description &&
                  openedModal === agreement.id &&
                  createPortal(
                    <Modal isOpen={true} onClose={handleModalClose}>
                      {agreement.description}
                    </Modal>,
                    document.body,
                    "agreementDescription" + agreement.id,
                  )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
}
